import { Divider, Stack, Typography } from '@mui/material';
import {
  type HighFrequencyBackupSchedules,
  type DlsgPolicyConfig,
  ConditionOperatorEnum,
  type DlsgPolicy,
  type ResourceIdentifiers,
} from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import useBackupVaults from '@/data/vaults/useBackupVaults';

import { Accordion, AccordionDetails, AccordionSummary } from '../accordion';
import OptionMenu from '../option-menu/option-menu';
import { Panels } from '../panels/panels';
import { resourceId } from '../queryBuilder/properties';
import { Icon } from '../shared/icon';
import { TextLink } from '../shared/textLink';
import { Tag } from '../tag';
import VaultTag from '../vaults/vaultTag';

export const StorageGatewayPolicy = ({
  policy,
  onUpdate,
}: {
  policy: DlsgPolicy;
  onUpdate?: () => void;
}) => {
  const dal = useDAL();
  const { rightPanel } = useWorkspace();
  const vaultId = policy.policyPlan?.backupSchedule?.vaultId;
  const { vaults } = useBackupVaults();
  const { data: resources } = dal.inventory.listResourceIdentifiers({
    property: resourceId.name,
    operator: ConditionOperatorEnum.In,
    value: policy.resourceInclusion,
  });

  const vault = vaults.find((v) => v.id === vaultId);
  if (!vault) {
    return null;
  }

  // const isAvailable = true;

  return (
    <Stack
      gap='24px'
      sx={{
        paddingY: '24px',
        border: '1px solid var(--mui-palette-divider)',
        borderRadius: '16px',
        backgroundColor: 'var(--mui-palette-background-paper)',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        margin={'0 40px'}
      >
        <Stack direction='row' alignItems='center' gap='16px'>
          <Typography variant='h3'>{policy.name}</Typography>
          {/* {isAvailable ? <SensorsOnIcon /> : <SensorsOffIcon />} */}
        </Stack>
        {onUpdate && (
          <OptionMenu
            options={['Edit']}
            onOptionSelected={(option) => {
              if (option === 'Edit') {
                rightPanel.setComponent({
                  panel: Panels.StorageGatewayWizard,
                  props: {
                    onSave: async () => {
                      onUpdate();
                    },
                    onClose: () => rightPanel.setIsOpen(false),
                    entity: policy,
                  },
                });
                rightPanel.setIsOpen(true, PanelSize.Full);
              }
            }}
          />
        )}
      </Stack>
      <Stack gap='12px' divider={<Divider />}>
        {/* <Accordion className='mx-[40px]'>
          <AccordionSummary>
            <Typography>Connection details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {isAvailable ? (
              <CopyableCode title='Gateway endpoint' code='Not available' />
            ) : (
              <Stack
                width='100%'
                height='120px'
                alignItems='center'
                justifyContent='center'
                className={`w-full p-[16px] bg-gray-100 rounded-[4px]`}
              >
                <Typography sx={{ width: '280px', textAlign: 'center' }}>
                  This gateway is unavailable.{' '}
                  <ContactSupportLink text='Contact support' color='primary' />{' '}
                  for assistance, we are here to help.
                </Typography>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion> */}
        <ConnectedResources
          resources={resources?.resources || []}
          isClickable={!!onUpdate}
        />
        <PolicyConfig policyConfigs={policy.policyPlan?.dlsgConfig || []} />
        <StorageAndRetention schedule={policy.policyPlan?.backupSchedule} />
      </Stack>
    </Stack>
  );
};

const ConnectedResources = ({
  resources,
  isClickable,
}: {
  resources: ResourceIdentifiers[];
  isClickable: boolean;
}) => {
  return (
    <Accordion className='mx-[40px]'>
      <AccordionSummary>
        <Typography>Connected resources</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap='24px'>
          <Typography>{resources.length} connected resources.</Typography>
          <Stack
            gap='12px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          >
            {resources.map((resource) => (
              <ResourceItem
                key={resource.id}
                resource={resource}
                isClickable={isClickable}
              />
            ))}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const ResourceItem = ({
  resource,
  isClickable,
}: {
  resource: ResourceIdentifiers;
  isClickable: boolean;
}) => {
  const { rightPanel } = useWorkspace();
  return isClickable ? (
    <TextLink
      onClick={() => {
        rightPanel.setComponent({
          panel: Panels.InventoryInstance,
          props: {
            id: resource.id,
            onClose: () => rightPanel.setIsOpen(false),
            onBack: () => rightPanel.setIsOpen(false),
            initialTab: 'overview',
          },
        });
        rightPanel.setIsOpen(true, PanelSize.Large);
      }}
      href={''}
    >
      {resource.name}
    </TextLink>
  ) : (
    <Typography>{resource.name}</Typography>
  );
};

const StorageAndRetention = ({
  schedule,
}: {
  schedule?: HighFrequencyBackupSchedules;
}) => {
  if (!schedule) {
    return null;
  }

  return (
    <Accordion className='mx-[40px]'>
      <AccordionSummary>
        <Typography>Storage and retention</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap='12px'>
          <RowWrapper label='Storage location'>
            <VaultTag vault={schedule?.vaultId} />
          </RowWrapper>
          <RowWrapper label='Retention'>
            <Typography>{schedule.backupRetentionDays} days</Typography>
          </RowWrapper>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const PolicyConfig = ({
  policyConfigs,
}: {
  policyConfigs: DlsgPolicyConfig[];
}) => {
  return (
    <Accordion className='mx-[40px]'>
      <AccordionSummary>
        <Typography>S3 prefixes</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap='40px' divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
          {policyConfigs.map((config, idx) => (
            <DlsgPolicyConfigItem key={idx} config={config} />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const DlsgPolicyConfigItem = ({ config }: { config: DlsgPolicyConfig }) => {
  return (
    <Stack gap='24px'>
      <Typography variant='subtitle1' fontWeight='medium'>
        Prefixes included
      </Typography>
      <Stack direction='row' gap='8px' flexWrap='wrap'>
        {config.pathPrefixes.map((prefix) => (
          <Tag key={prefix} content={prefix} variant='filled' />
        ))}
      </Stack>
      <Typography variant='subtitle1' fontWeight='medium'>
        Copy rules
      </Typography>
      <RowWrapper label='Object age'>
        <Typography>
          Older than {config.migrationDelayHours / 24} days
        </Typography>
      </RowWrapper>
      {config.isTruncateData && (
        <Stack gap='8px' direction='row' alignItems='center'>
          <Icon iconClass='material-symbols-check' />
          <Typography variant='subtitle1' fontWeight='medium'>
            Objects will be deleted in source after copy.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const RowWrapper = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <Stack gap='12px' direction='row' alignItems='center'>
      <Typography width='180px'>{label}</Typography>
      {children}
    </Stack>
  );
};
